import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
	type SxProps,
	type Theme,
} from '@mui/material';
import { t } from 'i18next';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '../../app/hooks';
import LeagueAvatar, { leagueLogoAvatarSx } from '../../components/custom/avatar/LeagueAvatar';
import CustomCalendarDialog from '../../components/custom/dialog/CustomCalendarDialog';
import MatchTeamsWithLogos from '../../components/MatchTeamsWithLogos';
import { showErrorSnackbar, showSuccessSnackbar } from '../../components/custom/snackbar/snackbarSlice';
import { teamApiLogoSrc } from '../admin/teams/teamFormUtils';
import { useFormatUserDateTime } from '../../shared/useFormatUserDateTime';
import { externalDataLayerAccent } from '../../shared/externalDataLayerColors';
import {
	deleteMonitoringRunsByLayer,
	fetchMonitoringLatest,
	fetchMonitoringRun,
	fetchMonitoringRuns,
	MONITORING_LAYERS,
	type ExternalDataLayer,
	type MonitoringCounters,
	type MonitoringFailedMatch,
	type MonitoringHttpLog,
	type MonitoringRun,
	type MonitoringStatus,
	type MonitoringTrigger,
} from './externalApiMonitoringApi';
import {
	monitoringDetailPanelSx,
	monitoringDetailTableSx,
	monitoringDurationColSx,
	monitoringHintSx,
	monitoringKpiCardSx,
	monitoringKpiGridSx,
	monitoringLayerTitleSx,
	monitoringLeagueColSx,
	monitoringMatchdayColSx,
	monitoringMobileHttpLogSx,
	monitoringMobileKpiCardSx,
	monitoringMobileKpiGridSx,
	monitoringMobileKpiLayerSx,
	monitoringMobileKpiMetaSx,
	monitoringMobileRootSx,
	monitoringMobileRunCardSx,
	monitoringMobileSectionHeaderSx,
	monitoringMobileStickySx,
	monitoringMobileTitleSx,
	monitoringMobileToolbarSx,
	monitoringPageRootSx,
	monitoringSectionHeaderSx,
	monitoringSectionSx,
	monitoringSectionTitleSx,
	monitoringTableContainerSx,
	monitoringTableSx,
	monitoringTitleSx,
	monitoringToolbarSx,
	statusAccent,
	statusChipSx,
} from './externalApiMonitoringPageStyles';

const LAYER_SHORT: Record<ExternalDataLayer, string> = {
	SCHEDULE: 'SCH',
	ODDS: 'ODDS',
	LIVE: 'LIVE',
	FULL_MATCH: 'FULL',
	STANDINGS: 'STD',
};

const EMPTY_LAYER_RUNS: Record<ExternalDataLayer, MonitoringRun[]> = {
	SCHEDULE: [],
	ODDS: [],
	LIVE: [],
	FULL_MATCH: [],
	STANDINGS: [],
};

type LayerTotals = { total: number; failed: number; hasMore: boolean };

const EMPTY_LAYER_TOTALS: Record<ExternalDataLayer, LayerTotals> = {
	SCHEDULE: { total: 0, failed: 0, hasMore: false },
	ODDS: { total: 0, failed: 0, hasMore: false },
	LIVE: { total: 0, failed: 0, hasMore: false },
	FULL_MATCH: { total: 0, failed: 0, hasMore: false },
	STANDINGS: { total: 0, failed: 0, hasMore: false },
};

const MONITORING_PAGE_LIMITS = [50, 100, 200, 500] as const;

function LayerRunCountLabel({
	shown,
	total,
	failed,
	offset = 0,
}: {
	shown: number;
	total: number;
	failed: number;
	offset?: number;
}): JSX.Element {
	return (
		<>
			<Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontWeight: 500 }}>
				({total})
			</Typography>
			{failed > 0 ? (
				<Typography
					component="span"
					sx={{ ml: 1, fontWeight: 600, color: statusAccent('FAILED'), fontSize: '0.82em' }}
				>
					{t('externalApiMonitoring.layerFailedCount', { failed })}
				</Typography>
			) : null}
			{shown < total ? (
				<Typography component="span" color="text.secondary" sx={{ ml: 1, fontWeight: 400, fontSize: '0.78em' }}>
					{t('externalApiMonitoring.layerCountRange', {
						from: total === 0 ? 0 : offset + 1,
						to: offset + shown,
						total,
					})}
				</Typography>
			) : null}
		</>
	);
}

function formatDuration(ms?: number | null): string {
	if (ms == null) return '—';
	if (ms < 1000) return `${ms} ms`;
	const totalSec = Math.floor(ms / 1000);
	if (totalSec < 60) return `${(ms / 1000).toFixed(1)} s`;
	const hours = Math.floor(totalSec / 3600);
	const minutes = Math.floor((totalSec % 3600) / 60);
	const seconds = totalSec % 60;
	if (hours > 0) return `${hours}h${minutes}m${seconds}s`;
	return `${minutes}m${seconds}s`;
}

function httpSuccessCount(failed?: number | null, total?: number | null): number {
	const tot = total ?? 0;
	const failedCount = failed ?? 0;
	return Math.max(0, tot - failedCount);
}

function formatHttpRatio(failed?: number | null, total?: number | null): string {
	const tot = total ?? 0;
	if (tot === 0) return '—';
	return `${httpSuccessCount(failed, tot)}/${tot}`;
}

function hasHttpFailures(failed?: number | null): boolean {
	return (failed ?? 0) > 0;
}

type CounterDetailKey =
	| 'upserted'
	| 'skipped'
	| 'skippedFar'
	| 'skippedNoBookieEvent'
	| 'skippedMissingKickoff'
	| 'roundsParsed'
	| 'eligible'
	| 'matched'
	| 'saved'
	| 'sseCalls'
	| 'updated'
	| 'finishedDetected'
	| 'requested'
	| 'mappingFailures';

function counterDetailEntries(
	layer: ExternalDataLayer,
	counters?: MonitoringCounters | null
): Array<{ key: CounterDetailKey; value: number }> {
	if (!counters) return [];
	switch (layer) {
		case 'SCHEDULE':
			return [
				{ key: 'upserted', value: counters.upserted ?? 0 },
				{ key: 'skipped', value: counters.skipped ?? 0 },
				{ key: 'roundsParsed', value: counters.roundsParsed ?? 0 },
			];
		case 'ODDS': {
			const hasSplit =
				counters.skippedFar != null ||
				counters.skippedNoBookieEvent != null ||
				counters.skippedMissingKickoff != null;
			const entries: Array<{ key: CounterDetailKey; value: number }> = [
				{ key: 'eligible', value: counters.eligible ?? 0 },
				{ key: 'matched', value: counters.matched ?? 0 },
				{ key: 'saved', value: counters.saved ?? 0 },
				{ key: 'sseCalls', value: counters.sseCalls ?? 0 },
			];
			if (hasSplit) {
				entries.push(
					{ key: 'skippedFar', value: counters.skippedFar ?? 0 },
					{ key: 'skippedNoBookieEvent', value: counters.skippedNoBookieEvent ?? 0 },
					{ key: 'skippedMissingKickoff', value: counters.skippedMissingKickoff ?? 0 }
				);
			} else {
				entries.push({ key: 'skipped', value: counters.skipped ?? 0 });
			}
			if (counters.mappingFailures) {
				entries.push({ key: 'mappingFailures', value: counters.mappingFailures });
			}
			return entries;
		}
		case 'LIVE': {
			const liveEntries: Array<{ key: CounterDetailKey; value: number }> = [
				{ key: 'updated', value: counters.updated ?? 0 },
				{ key: 'finishedDetected', value: counters.finishedDetected ?? 0 },
				{ key: 'skipped', value: counters.skipped ?? 0 },
			];
			if (counters.mappingFailures) {
				liveEntries.push({ key: 'mappingFailures', value: counters.mappingFailures });
			}
			return liveEntries;
		}
		case 'FULL_MATCH':
			return [
				{ key: 'requested', value: counters.requested ?? 0 },
				{ key: 'saved', value: counters.saved ?? 0 },
				{ key: 'skipped', value: counters.skipped ?? 0 },
			];
		default:
			return [];
	}
}

function oddsSkipSummary(counters: MonitoringCounters): string {
	const hasSplit =
		counters.skippedFar != null ||
		counters.skippedNoBookieEvent != null ||
		counters.skippedMissingKickoff != null;
	if (!hasSplit) {
		return `skip ${counters.skipped ?? 0}`;
	}
	const far = counters.skippedFar ?? 0;
	const noBk = counters.skippedNoBookieEvent ?? 0;
	const missKo = counters.skippedMissingKickoff ?? 0;
	let s = `far ${far} · noBk ${noBk}`;
	if (missKo > 0) {
		s += ` · missKo ${missKo}`;
	}
	return s;
}

function countersSummary(layer: ExternalDataLayer, counters?: MonitoringCounters | null): string {
	if (!counters) return '—';
	switch (layer) {
		case 'SCHEDULE':
			return `↑${counters.upserted ?? 0} · skip ${counters.skipped ?? 0} · rounds ${counters.roundsParsed ?? 0}`;
		case 'ODDS':
			return `elig ${counters.eligible ?? 0} · match ${counters.matched ?? 0} · save ${counters.saved ?? 0} · SSE ${counters.sseCalls ?? 0} · ${oddsSkipSummary(counters)}`;
		case 'LIVE': {
			const base = `upd ${counters.updated ?? 0} · fin ${counters.finishedDetected ?? 0} · skip ${counters.skipped ?? 0}`;
			const mapF = counters.mappingFailures ?? 0;
			return mapF > 0 ? `${base} · mapF ${mapF}` : base;
		}
		case 'FULL_MATCH':
			return `req ${counters.requested ?? 0} · save ${counters.saved ?? 0} · skip ${counters.skipped ?? 0}`;
		default:
			return '—';
	}
}

function statusLabel(status?: MonitoringStatus | null): string {
	if (!status) return '—';
	const key = `externalApiMonitoring.status.${status}`;
	const translated = t(key);
	return translated !== key ? translated : status;
}

function layerTitle(layer: ExternalDataLayer): string {
	return t(`externalApiMonitoring.layer.${layer}`);
}

/** Soft monitoring reasons — amber warning, not hard failure. */
const MONITORING_WARNING_KEYS = new Set([
	'missingUtcKickoff',
	'alreadyFetched',
	'farKickoffDb',
	'noLiveCandidates',
	'noSlots',
	'leagueNotSupported',
	'noSseEligible',
	'noCurrentMatches',
	'farBothComplete',
	'farCurrentCompleteNoNext',
	'currentExhaustedNoNext',
	'currentExhaustedNextComplete',
	'invalidInput',
	'currentMissingKickoff',
]);

function formatOddsMatchday(run: MonitoringRun): string {
	if (run.slotOrders != null && run.slotOrders.length > 0) {
		return run.slotOrders.join(', ');
	}
	if (run.matchday != null) {
		return String(run.matchday);
	}
	return '—';
}

function formatOddsSlotScope(scope?: string | null): string | null {
	if (!scope) return null;
	const key = `externalApiMonitoring.slotScope.${scope}`;
	const translated = t(key);
	return translated !== key ? translated : scope;
}

function parseMonitoringReasonParts(
	summary: string
): Array<{ key: string; count?: number; labels?: string; raw: string }> {
	return summary
		.split(';')
		.map((part) => part.trim())
		.filter(Boolean)
		.map((raw) => {
			const match = /^([a-zA-Z][a-zA-Z0-9]*)(?:=(\d+))?(?:\s*\[([^\]]*)\])?$/.exec(raw);
			if (!match) {
				return { key: raw, raw };
			}
			const labels = match[3]?.trim();
			return {
				key: match[1],
				count: match[2] != null ? Number(match[2]) : undefined,
				labels: labels || undefined,
				raw,
			};
		});
}

function formatMonitoringReason(summary?: string | null): string | null {
	if (!summary || !summary.trim()) return null;
	return parseMonitoringReasonParts(summary)
		.map(({ key, count, labels, raw }) => {
			const i18nKey = `externalApiMonitoring.reason.${key}`;
			const translated = t(i18nKey, { count: count ?? 0 });
			const base =
				translated !== i18nKey
					? translated
					: raw.replace(/\s*\[[^\]]*]\s*$/, '').trim() || raw;
			// Team names for mappingFailures are shown with logos separately.
			if (key === 'mappingFailures') {
				return base;
			}
			return labels ? `${base} [${labels}]` : base;
		})
		.join(' · ');
}

function isMonitoringWarningSummary(summary?: string | null): boolean {
	if (!summary) return false;
	const parts = parseMonitoringReasonParts(summary);
	return parts.length > 0 && parts.every((p) => MONITORING_WARNING_KEYS.has(p.key));
}

function failedMatchesFromDetail(detail: MonitoringRun): MonitoringFailedMatch[] {
	if (detail.failedMatches && detail.failedMatches.length > 0) {
		return detail.failedMatches;
	}
	const labels = detail.failedMatchLabels?.filter((x) => x && x.trim()) ?? [];
	if (labels.length > 0) {
		return labels.map((label) => {
			const parts = label.split(/\s+-\s+/);
			return {
				homeTitle: parts[0]?.trim() || label,
				awayTitle: parts[1]?.trim() || '',
			};
		});
	}
	return [];
}

function httpLogTeams(
	log: MonitoringHttpLog
): {
	homeTitle?: string;
	awayTitle?: string;
	homeLogoKey?: string;
	awayLogoKey?: string;
} | null {
	if (log.homeTitle || log.awayTitle) {
		return {
			homeTitle: log.homeTitle || undefined,
			awayTitle: log.awayTitle || undefined,
			homeLogoKey: log.homeLogoKey || undefined,
			awayLogoKey: log.awayLogoKey || undefined,
		};
	}
	if (log.teams?.trim()) {
		const parts = log.teams.split(/\s+-\s+/);
		return {
			homeTitle: parts[0]?.trim() || log.teams.trim(),
			awayTitle: parts[1]?.trim() || '',
		};
	}
	return null;
}

function ProviderCell({ provider }: { provider?: string | null }): JSX.Element {
	if (!provider) {
		return <Typography component="span">—</Typography>;
	}
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
			<Avatar
				variant="square"
				alt=""
				src={teamApiLogoSrc(provider)}
				sx={[{ width: 18, height: 18, flexShrink: 0 }, leagueLogoAvatarSx] as SxProps<Theme>}
			/>
			<Typography component="span" sx={{ fontSize: 'inherit' }}>
				{provider}
			</Typography>
		</Box>
	);
}

const TRIGGER_ICON_SX = { fontSize: 16, opacity: 0.75, flexShrink: 0 } as const;

function TriggerCell({
	trigger,
	layer,
	iconOnly = false,
}: {
	trigger?: MonitoringTrigger | null;
	layer: ExternalDataLayer;
	iconOnly?: boolean;
}): JSX.Element {
	if (!trigger) {
		return <Typography component="span">—</Typography>;
	}
	const icon =
		trigger === 'ADMIN' ? (
			<PersonOutlineIcon
				sx={{ ...TRIGGER_ICON_SX, opacity: 1, color: externalDataLayerAccent(layer) }}
			/>
		) : trigger === 'CRON' ? (
			<AccessTimeIcon sx={TRIGGER_ICON_SX} />
		) : null;
	if (!icon) {
		return <Typography component="span">{trigger}</Typography>;
	}
	if (iconOnly) {
		return (
			<Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }} title={trigger}>
				{icon}
			</Box>
		);
	}
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
			{icon}
			<Typography component="span" sx={{ fontSize: 'inherit' }}>
				{trigger}
			</Typography>
		</Box>
	);
}

function LeagueCell({ leagueCode }: { leagueCode?: string | null }): JSX.Element {
	if (!leagueCode) {
		return <Typography component="span">—</Typography>;
	}
	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 0.85,
				minHeight: 28,
			}}
		>
			<LeagueAvatar leagueCode={leagueCode} height={18} sx={{ mr: 0 }} avasx={{ mr: 0.75 }} />
		</Box>
	);
}

function OddsMatchdayCell({ run }: { run: MonitoringRun }): JSX.Element {
	const matchday = formatOddsMatchday(run);
	const scope = formatOddsSlotScope(run.slotScope);
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				justifyContent: 'center',
				gap: 0.4,
				minHeight: 28,
			}}
		>
			<Typography
				component="span"
				sx={{
					fontWeight: 700,
					fontSize: '0.84rem',
					lineHeight: 1.15,
					fontVariantNumeric: 'tabular-nums',
					letterSpacing: '0.03em',
				}}
			>
				{matchday}
			</Typography>
			{scope ? (
				<Typography
					component="span"
					sx={{
						fontSize: '0.62rem',
						fontWeight: 700,
						lineHeight: 1,
						letterSpacing: '0.08em',
						textTransform: 'uppercase',
						color: 'text.secondary',
						opacity: 0.9,
					}}
				>
					{scope}
				</Typography>
			) : null}
		</Box>
	);
}

function CountersBreakdown({
	layer,
	counters,
}: {
	layer: ExternalDataLayer;
	counters?: MonitoringCounters | null;
}): JSX.Element | null {
	const entries = counterDetailEntries(layer, counters);
	if (entries.length === 0) return null;
	return (
		<Box sx={{ mb: 1.25 }}>
			<Typography
				sx={{
					fontSize: '0.72rem',
					fontWeight: 700,
					textTransform: 'uppercase',
					letterSpacing: '0.04em',
					color: 'text.secondary',
					mb: 0.75,
				}}
			>
				{t('externalApiMonitoring.counters.title')}
			</Typography>
			<Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
				{entries.map(({ key, value }) => (
					<Typography
						component="li"
						key={key}
						sx={{ fontSize: '0.78rem', lineHeight: 1.35, color: 'text.primary' }}
					>
						<strong>{value}</strong>
						{' — '}
						{t(`externalApiMonitoring.counters.${layer}.${key}`)}
					</Typography>
				))}
			</Box>
		</Box>
	);
}

function MobileHttpLogs({
	logs,
}: {
	logs: NonNullable<MonitoringRun['httpLogs']>;
}): JSX.Element {
	return (
		<Box sx={{ mt: 0.5 }}>
			{logs.map((log, idx) => (
				<Box key={`http-${idx}`} sx={monitoringMobileHttpLogSx}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'baseline',
							justifyContent: 'space-between',
							gap: 0.75,
							flexWrap: 'wrap',
						}}
					>
						<Typography
							sx={{
								fontSize: '0.72rem',
								fontWeight: 700,
								lineHeight: 1.25,
								minWidth: 0,
								wordBreak: 'break-word',
							}}
						>
							{log.requestType ?? '—'}
							{log.httpStatus != null ? ` · ${log.httpStatus}` : ''}
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
							<Typography
								sx={{
									fontSize: '0.68rem',
									fontWeight: 700,
									color: log.outcome && log.outcome !== 'SUCCESS' ? '#f43f5e' : '#22c55e',
								}}
							>
								{log.outcome ?? '—'}
							</Typography>
							<Typography
								sx={{
									fontSize: '0.68rem',
									color: 'text.secondary',
									fontVariantNumeric: 'tabular-nums',
								}}
							>
								{formatDuration(log.durationMs)}
							</Typography>
						</Box>
					</Box>
					{log.target ? (
						<Typography
							sx={{
								mt: 0.25,
								fontSize: '0.68rem',
								lineHeight: 1.3,
								color: 'text.secondary',
								wordBreak: 'break-all',
							}}
						>
							{log.target}
						</Typography>
					) : null}
					{(() => {
						const teams = httpLogTeams(log);
						return teams ? (
							<Box sx={{ mt: 0.35 }}>
								<MatchTeamsWithLogos
									home={{ title: teams.homeTitle, logoKey: teams.homeLogoKey }}
									away={{ title: teams.awayTitle, logoKey: teams.awayLogoKey }}
									height={16}
									sx={{ fontSize: '0.72rem' }}
								/>
							</Box>
						) : null;
					})()}
					{log.detail ? (
						<Typography
							sx={{
								mt: 0.2,
								fontSize: '0.68rem',
								lineHeight: 1.3,
								color: 'text.secondary',
								wordBreak: 'break-word',
								whiteSpace: 'normal',
							}}
						>
							{log.detail}
						</Typography>
					) : null}
				</Box>
			))}
		</Box>
	);
}

export default function ExternalApiMonitoringPage(): JSX.Element {
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
	const dispatch = useAppDispatch();
	const { formatDetailed, formatDateTime, formatTime } = useFormatUserDateTime();
	const [hours, setHours] = useState(72);
	const [pageLimit, setPageLimit] = useState<number>(100);
	const [errorsOnly, setErrorsOnly] = useState(false);
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [runsByLayer, setRunsByLayer] = useState<Record<ExternalDataLayer, MonitoringRun[]>>(EMPTY_LAYER_RUNS);
	const [totalsByLayer, setTotalsByLayer] = useState<Record<ExternalDataLayer, LayerTotals>>(EMPTY_LAYER_TOTALS);
	const [latest, setLatest] = useState<Partial<Record<ExternalDataLayer, MonitoringRun>>>({});
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [detail, setDetail] = useState<MonitoringRun | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [deleteLayer, setDeleteLayer] = useState<ExternalDataLayer | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [mobileLayer, setMobileLayer] = useState<ExternalDataLayer>('SCHEDULE');

	const reload = useCallback(async (): Promise<void> => {
		setLoading(true);
		try {
			const statusFilter = errorsOnly ? 'issues' : undefined;
			const query = { hours, limit: pageLimit, offset: 0, status: statusFilter };
			const [latestMap, pages] = await Promise.all([
				fetchMonitoringLatest(),
				Promise.all(MONITORING_LAYERS.map((layer) => fetchMonitoringRuns(layer, query))),
			]);
			setLatest(latestMap);
			setOffset(0);
			const nextRuns: Record<ExternalDataLayer, MonitoringRun[]> = { ...EMPTY_LAYER_RUNS };
			const nextTotals: Record<ExternalDataLayer, LayerTotals> = { ...EMPTY_LAYER_TOTALS };
			MONITORING_LAYERS.forEach((layer, idx) => {
				const page = pages[idx];
				nextRuns[layer] = page.runs;
				nextTotals[layer] = {
					total: page.total,
					failed: page.failed,
					hasMore: page.hasMore ?? false,
				};
			});
			setRunsByLayer(nextRuns);
			setTotalsByLayer(nextTotals);
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalApiMonitoringLoadFailed',
				})
			);
		} finally {
			setLoading(false);
		}
	}, [dispatch, errorsOnly, hours, pageLimit]);

	const loadMore = useCallback(async (): Promise<void> => {
		setLoadingMore(true);
		const nextOffset = offset + pageLimit;
		try {
			const statusFilter = errorsOnly ? 'issues' : undefined;
			const query = { hours, limit: pageLimit, offset: nextOffset, status: statusFilter };
			const pages = await Promise.all(MONITORING_LAYERS.map((layer) => fetchMonitoringRuns(layer, query)));
			setOffset(nextOffset);
			setRunsByLayer((prev) => {
				const nextRuns = { ...prev };
				MONITORING_LAYERS.forEach((layer, idx) => {
					nextRuns[layer] = [...(prev[layer] ?? []), ...pages[idx].runs];
				});
				return nextRuns;
			});
			setTotalsByLayer((prev) => {
				const nextTotals = { ...prev };
				MONITORING_LAYERS.forEach((layer, idx) => {
					const page = pages[idx];
					nextTotals[layer] = {
						total: page.total,
						failed: page.failed,
						hasMore: page.hasMore ?? false,
					};
				});
				return nextTotals;
			});
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalApiMonitoringLoadFailed',
				})
			);
		} finally {
			setLoadingMore(false);
		}
	}, [dispatch, errorsOnly, hours, offset, pageLimit]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const openDetail = async (id: string): Promise<void> => {
		setExpandedId(id);
		setDetailLoading(true);
		setDetail(null);
		try {
			const run = await fetchMonitoringRun(id);
			setDetail(run);
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalApiMonitoringLoadFailed',
				})
			);
			setExpandedId(null);
		} finally {
			setDetailLoading(false);
		}
	};

	const handleDeleteLayer = async (): Promise<void> => {
		if (!deleteLayer) return;
		setDeleting(true);
		try {
			const deleted = await deleteMonitoringRunsByLayer(deleteLayer);
			setExpandedId(null);
			setDetail(null);
			setDeleteLayer(null);
			dispatch(
				showSuccessSnackbar({
					message: t('externalApiMonitoring.layerCleared', {
						layer: layerTitle(deleteLayer),
						count: deleted,
					}),
				})
			);
			await reload();
		} catch (error) {
			dispatch(
				showErrorSnackbar({
					message: error instanceof Error ? error.message : 'externalApiMonitoringDeleteFailed',
				})
			);
		} finally {
			setDeleting(false);
		}
	};

	if (isCompact) {
		const mobileRows = runsByLayer[mobileLayer];
		const mobileTotals = totalsByLayer[mobileLayer];
		return (
			<Box sx={monitoringMobileRootSx}>
				<Box sx={monitoringMobileStickySx}>
					<Box sx={monitoringMobileToolbarSx}>
						<Typography sx={monitoringMobileTitleSx} noWrap>
							{t('externalApiMonitoring.menuLink')}
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
							<Select
								size="small"
								value={hours}
								onChange={(e) => setHours(Number(e.target.value))}
								sx={{
									minWidth: 84,
									height: 40,
									fontSize: '0.8rem',
									'& .MuiSelect-select': { py: 0.75, px: 1.25 },
								}}
							>
								<MenuItem value={24}>24h</MenuItem>
								<MenuItem value={72}>72h</MenuItem>
								<MenuItem value={168}>7d</MenuItem>
							</Select>
							<Tooltip title={t('externalApiMonitoring.refresh')}>
								<span>
									<IconButton
										onClick={() => void reload()}
										disabled={loading}
										sx={{ minWidth: 40, minHeight: 40 }}
									>
										{loading ? <CircularProgress size={18} /> : <RefreshIcon />}
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					</Box>
					<Box sx={monitoringMobileKpiGridSx}>
						{MONITORING_LAYERS.map((layer) => {
							const run = latest[layer];
							const selected = mobileLayer === layer;
							return (
									<Box
										key={layer}
										role="button"
										tabIndex={0}
										aria-pressed={selected}
										aria-label={layerTitle(layer)}
										title={layerTitle(layer)}
									onClick={() => {
										setMobileLayer(layer);
										setExpandedId(null);
										setDetail(null);
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setMobileLayer(layer);
											setExpandedId(null);
											setDetail(null);
										}
									}}
									sx={monitoringMobileKpiCardSx(theme, layer, run?.status, selected)}
								>
									<Typography
										sx={[
											monitoringMobileKpiLayerSx,
											{ color: externalDataLayerAccent(layer) },
										] as SxProps<Theme>}
									>
										{LAYER_SHORT[layer]}
									</Typography>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 0.4,
											pl: 0.35,
											mt: 0.25,
											minWidth: 0,
										}}
									>
										{run ? (
											<Box
												sx={{
													width: 6,
													height: 6,
													borderRadius: '50%',
													flexShrink: 0,
													bgcolor: statusAccent(run.status),
												}}
											/>
										) : null}
										<Typography
											sx={[monitoringMobileKpiMetaSx, { pl: 0, mt: 0, flex: 1 }] as SxProps<Theme>}
										>
											{run ? formatTime(run.startedAt) : '—'}
										</Typography>
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>

				<Box sx={monitoringMobileSectionHeaderSx(theme, mobileLayer)}>
					<Typography sx={monitoringSectionTitleSx(mobileLayer)}>
						{layerTitle(mobileLayer)}
						<LayerRunCountLabel
							shown={mobileRows.length}
							total={mobileTotals.total}
							failed={mobileTotals.failed}
						/>
					</Typography>
					<Tooltip title={t('externalApiMonitoring.deleteLayer')}>
						<span>
							<IconButton
								aria-label={t('externalApiMonitoring.deleteLayer')}
								onClick={() => setDeleteLayer(mobileLayer)}
								disabled={loading || deleting}
								sx={{ minWidth: 40, minHeight: 40 }}
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>

				{loading && mobileRows.length === 0 ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={24} />
					</Box>
				) : mobileRows.length === 0 ? (
					<Typography color="text.secondary" sx={{ px: 1, py: 2, fontSize: '0.85rem' }}>
						{t('externalApiMonitoring.noRuns')}
					</Typography>
				) : (
					mobileRows.map((run) => {
						const open = expandedId === run.id;
						const warning = isMonitoringWarningSummary(run.errorSummary);
						const oddsScope =
							mobileLayer === 'ODDS' ? formatOddsSlotScope(run.slotScope) : null;
						return (
							<Box
								key={run.id}
								sx={monitoringMobileRunCardSx(theme, mobileLayer, open)}
								onClick={() => {
									if (open) {
										setExpandedId(null);
										setDetail(null);
									} else {
										void openDetail(run.id);
									}
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
									<ExpandMoreIcon
										sx={{
											fontSize: 18,
											mt: 0.15,
											transform: open ? 'rotate(180deg)' : 'none',
											transition: 'transform 0.2s',
											opacity: 0.7,
											flexShrink: 0,
										}}
									/>
									<Box sx={{ minWidth: 0, flex: 1 }}>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												gap: 0.75,
											}}
										>
											<Typography
												sx={{
													fontSize: '0.78rem',
													fontWeight: 700,
													lineHeight: 1.25,
													fontVariantNumeric: 'tabular-nums',
													minWidth: 0,
												}}
											>
												{formatDateTime(run.startedAt)}
											</Typography>
											<Chip
												size="small"
												variant="outlined"
												label={statusLabel(run.status)}
												sx={statusChipSx(run.status)}
											/>
										</Box>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 0.6,
												mt: 0.4,
												minWidth: 0,
											}}
										>
											<TriggerCell trigger={run.trigger} layer={mobileLayer} iconOnly />
											<Box
												sx={{
													minWidth: 0,
													flex: 1,
													overflow: 'hidden',
													'& > .MuiBox-root': { minWidth: 0, overflow: 'hidden' },
													'& .MuiTypography-root': {
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													},
												}}
											>
												<ProviderCell provider={run.provider} />
											</Box>
											<LeagueCell leagueCode={run.leagueCode} />
										</Box>
										<Typography
											sx={{
												mt: 0.25,
												fontSize: '0.72rem',
												fontWeight: 600,
												fontVariantNumeric: 'tabular-nums',
												color: 'text.secondary',
											}}
										>
											{formatDuration(run.durationMs)}
											{' · '}
											{t('externalApiMonitoring.col.http')}{' '}
											<Box
												component="span"
												sx={{
													color: hasHttpFailures(run.httpRequestsFailed)
														? '#f43f5e'
														: 'text.secondary',
													fontWeight: hasHttpFailures(run.httpRequestsFailed) ? 700 : 600,
												}}
											>
												{formatHttpRatio(run.httpRequestsFailed, run.httpRequestsTotal)}
											</Box>
											{mobileLayer === 'ODDS' ? (
												<>
													{' · '}
													{formatOddsMatchday(run)}
													{oddsScope ? ` ${oddsScope}` : ''}
												</>
											) : null}
										</Typography>
										<Typography
											sx={{
												mt: 0.35,
												fontSize: '0.7rem',
												lineHeight: 1.3,
												color: 'text.secondary',
												wordBreak: 'break-word',
											}}
										>
											{countersSummary(mobileLayer, run.counters)}
										</Typography>
										{run.errorSummary ? (
											<Typography
												sx={{
													mt: 0.35,
													fontSize: '0.7rem',
													lineHeight: 1.3,
													color: warning ? '#d97706' : '#f43f5e',
													fontWeight: 600,
													whiteSpace: 'normal',
													wordBreak: 'break-word',
												}}
											>
												{formatMonitoringReason(run.errorSummary)}
											</Typography>
										) : null}
									</Box>
								</Box>
								<Collapse in={open} timeout="auto" unmountOnExit>
									<Box
										sx={{ pt: 1, mt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
										onClick={(e) => e.stopPropagation()}
									>
										{detailLoading && expandedId === run.id ? (
											<CircularProgress size={18} />
										) : detail && detail.id === run.id ? (
											<Box>
												{mobileLayer === 'ODDS' ? (
													<Typography
														variant="caption"
														color="text.secondary"
														display="block"
														sx={{ mb: 1 }}
													>
														{t('externalApiMonitoring.oddsSlotDetail', {
															matchday: formatOddsMatchday(detail),
															scope: formatOddsSlotScope(detail.slotScope) ?? '—',
														})}
													</Typography>
												) : null}
												{detail.errorSummary ? (
													<Typography
														sx={{
															mb: 1,
															fontSize: '0.78rem',
															color: isMonitoringWarningSummary(detail.errorSummary)
																? '#d97706'
																: '#f43f5e',
															fontWeight: 600,
															whiteSpace: 'normal',
															wordBreak: 'break-word',
														}}
													>
														{formatMonitoringReason(detail.errorSummary)}
													</Typography>
												) : null}
												<CountersBreakdown
													layer={mobileLayer}
													counters={detail.counters ?? run.counters}
												/>
												{failedMatchesFromDetail(detail).length > 0 ? (
													<Box sx={{ mb: 1 }}>
														<Typography
															variant="caption"
															color="text.secondary"
															display="block"
															sx={{ mb: 0.5 }}
														>
															{t('externalApiMonitoring.failedMatches')}:
														</Typography>
														<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
															{failedMatchesFromDetail(detail).map((m, idx) => (
																<MatchTeamsWithLogos
																	key={`${m.matchScheduleId ?? 'm'}-${idx}`}
																	home={{ title: m.homeTitle, logoKey: m.homeLogoKey }}
																	away={{ title: m.awayTitle, logoKey: m.awayLogoKey }}
																	height={16}
																	sx={{ fontSize: '0.78rem' }}
																/>
															))}
														</Box>
													</Box>
												) : null}
												{(detail.httpLogs?.length ?? 0) === 0 ? (
													<Typography variant="caption" color="text.secondary">
														{t('externalApiMonitoring.noHttpLogs')}
													</Typography>
												) : (
													<MobileHttpLogs logs={detail.httpLogs!} />
												)}
											</Box>
										) : null}
									</Box>
								</Collapse>
							</Box>
						);
					})
				)}

				<CustomCalendarDialog
					open={deleteLayer != null}
					onClose={() => {
						if (!deleting) setDeleteLayer(null);
					}}
					onSave={() => void handleDeleteLayer()}
					title={
						deleteLayer
							? t('externalApiMonitoring.deleteLayerTitle', { layer: layerTitle(deleteLayer) })
							: undefined
					}
					helperText={
						deleteLayer
							? t('externalApiMonitoring.deleteLayerHelper', { layer: layerTitle(deleteLayer) })
							: undefined
					}
					buttonAcceptText={t('externalApiMonitoring.deleteLayerConfirm')}
					submitting={deleting}
				/>
			</Box>
		);
	}

	return (
		<Box sx={monitoringPageRootSx}>
			<Typography sx={monitoringTitleSx}>{t('externalApiMonitoring.title')}</Typography>
			<Typography sx={monitoringHintSx}>{t('externalApiMonitoring.hint')}</Typography>

			<Box sx={monitoringToolbarSx}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Typography variant="body2" color="text.secondary">
						{t('externalApiMonitoring.period')}
					</Typography>
					<Select
						size="small"
						value={hours}
						onChange={(e) => setHours(Number(e.target.value))}
						sx={{ minWidth: 120 }}
					>
						<MenuItem value={24}>24h</MenuItem>
						<MenuItem value={72}>72h</MenuItem>
						<MenuItem value={168}>7d</MenuItem>
					</Select>
					<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
						{t('externalApiMonitoring.pageSize')}
					</Typography>
					<Select
						size="small"
						value={pageLimit}
						onChange={(e) => setPageLimit(Number(e.target.value))}
						sx={{ minWidth: 88 }}
					>
						{MONITORING_PAGE_LIMITS.map((value) => (
							<MenuItem key={value} value={value}>
								{value}
							</MenuItem>
						))}
					</Select>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={errorsOnly}
								onChange={(e) => setErrorsOnly(e.target.checked)}
							/>
						}
						label={t('externalApiMonitoring.errorsOnly')}
						sx={{ ml: 0.5, mr: 0 }}
					/>
				</Box>
				<Tooltip title={t('externalApiMonitoring.refresh')}>
					<span>
						<IconButton onClick={() => void reload()} disabled={loading} sx={{ minWidth: 40, minHeight: 40 }}>
							{loading ? <CircularProgress size={18} /> : <RefreshIcon />}
						</IconButton>
					</span>
				</Tooltip>
			</Box>

			<Box sx={monitoringKpiGridSx}>
				{MONITORING_LAYERS.map((layer) => {
					const run = latest[layer];
					const totals = totalsByLayer[layer];
					return (
						<Box key={layer} sx={monitoringKpiCardSx(theme, layer, run?.status)}>
							<Typography sx={monitoringLayerTitleSx(layer)}>{layerTitle(layer)}</Typography>
							{run ? (
								<>
									<Chip
										size="small"
										variant="outlined"
										label={statusLabel(run.status)}
										sx={statusChipSx(run.status)}
									/>
									<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
										{run.provider ?? '—'} · {formatDetailed(run.startedAt)}
									</Typography>
									{totals.total > 0 ? (
										<Typography variant="caption" color="text.secondary" display="block">
											{t('externalApiMonitoring.layerPeriodTotals', {
												total: totals.total,
												failed: totals.failed,
											})}
										</Typography>
									) : null}
								</>
							) : (
								<Typography variant="caption" color="text.secondary">
									{t('externalApiMonitoring.noRuns')}
								</Typography>
							)}
						</Box>
					);
				})}
			</Box>

			{MONITORING_LAYERS.map((layer) => {
				const rows = runsByLayer[layer];
				const totals = totalsByLayer[layer];
				return (
					<Box key={layer} sx={monitoringSectionSx(theme, layer)}>
						<Box sx={monitoringSectionHeaderSx(theme, layer)}>
							<Typography sx={monitoringSectionTitleSx(layer)}>
								{layerTitle(layer)}
								<LayerRunCountLabel
									shown={rows.length}
									total={totals.total}
									failed={totals.failed}
									offset={offset}
								/>
							</Typography>
							<Tooltip title={t('externalApiMonitoring.deleteLayer')}>
								<span>
									<IconButton
										aria-label={t('externalApiMonitoring.deleteLayer')}
										onClick={() => setDeleteLayer(layer)}
										disabled={loading || deleting}
										sx={{ minWidth: 40, minHeight: 40 }}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Box>
						{loading && rows.length === 0 ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
								<CircularProgress size={24} />
							</Box>
						) : rows.length === 0 ? (
							<Typography color="text.secondary" sx={{ px: 2, py: 2, fontSize: '0.85rem' }}>
								{t('externalApiMonitoring.noRuns')}
							</Typography>
						) : (
							<TableContainer
								sx={monitoringTableContainerSx(rows.some((r) => r.id === expandedId))}
							>
								<Table size="small" stickyHeader sx={monitoringTableSx(theme, layer)}>
									<TableHead>
										<TableRow>
											<TableCell />
											<TableCell>{t('externalApiMonitoring.col.started')}</TableCell>
											<TableCell>{t('externalApiMonitoring.col.trigger')}</TableCell>
											<TableCell>{t('externalApiMonitoring.col.provider')}</TableCell>
											<TableCell sx={monitoringLeagueColSx}>
												{t('externalApiMonitoring.col.league')}
											</TableCell>
											{layer === 'ODDS' ? (
												<TableCell sx={monitoringMatchdayColSx}>
													{t('externalApiMonitoring.col.matchday')}
												</TableCell>
											) : null}
											<TableCell sx={monitoringDurationColSx}>
												{t('externalApiMonitoring.col.duration')}
											</TableCell>
											<TableCell>{t('externalApiMonitoring.col.status')}</TableCell>
											<TableCell>{t('externalApiMonitoring.col.counters')}</TableCell>
											<TableCell>{t('externalApiMonitoring.col.http')}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rows.map((run) => {
											const open = expandedId === run.id;
											const colSpan = layer === 'ODDS' ? 10 : 9;
											return (
												<Fragment key={run.id}>
													<TableRow
														hover
														sx={{ cursor: 'pointer' }}
														onClick={() => {
															if (open) {
																setExpandedId(null);
																setDetail(null);
															} else {
																void openDetail(run.id);
															}
														}}
													>
														<TableCell sx={{ width: 36 }}>
															<ExpandMoreIcon
																sx={{
																	fontSize: 18,
																	transform: open ? 'rotate(180deg)' : 'none',
																	transition: 'transform 0.2s',
																	opacity: 0.7,
																}}
															/>
														</TableCell>
														<TableCell>{formatDetailed(run.startedAt)}</TableCell>
														<TableCell>
															<TriggerCell trigger={run.trigger} layer={layer} />
														</TableCell>
														<TableCell>
															<ProviderCell provider={run.provider} />
														</TableCell>
														<TableCell sx={monitoringLeagueColSx}>
															<LeagueCell leagueCode={run.leagueCode} />
														</TableCell>
														{layer === 'ODDS' ? (
															<TableCell sx={monitoringMatchdayColSx}>
																<OddsMatchdayCell run={run} />
															</TableCell>
														) : null}
														<TableCell sx={monitoringDurationColSx}>
															{formatDuration(run.durationMs)}
														</TableCell>
														<TableCell>
															<Chip
																size="small"
																variant="outlined"
																label={statusLabel(run.status)}
																sx={statusChipSx(run.status)}
															/>
															{run.errorSummary ? (
																<Typography
																	sx={{
																		mt: 0.5,
																		fontSize: '0.7rem',
																		lineHeight: 1.3,
																		color: isMonitoringWarningSummary(run.errorSummary)
																			? '#d97706'
																			: '#f43f5e',
																		maxWidth: 220,
																		whiteSpace: 'normal',
																	}}
																>
																	{formatMonitoringReason(run.errorSummary)}
																</Typography>
															) : null}
														</TableCell>
														<TableCell>{countersSummary(layer, run.counters)}</TableCell>
														<TableCell>
															<Typography
																component="span"
																sx={{
																	color: hasHttpFailures(run.httpRequestsFailed)
																		? '#f43f5e'
																		: 'text.secondary',
																	fontWeight: hasHttpFailures(run.httpRequestsFailed) ? 700 : 500,
																}}
															>
																{formatHttpRatio(run.httpRequestsFailed, run.httpRequestsTotal)}
															</Typography>
														</TableCell>
													</TableRow>
													<TableRow
														sx={{
															display: open ? 'table-row' : 'none',
															'&:hover': { background: 'transparent' },
														}}
													>
														<TableCell
															colSpan={colSpan}
															sx={{
																py: 0,
																borderBottom: open ? undefined : 0,
																background: 'transparent',
															}}
														>
															<Collapse in={open} timeout="auto" unmountOnExit>
																<Box sx={monitoringDetailPanelSx(theme, layer)}>
																	{detailLoading && expandedId === run.id ? (
																		<CircularProgress size={18} />
																	) : detail && detail.id === run.id ? (
																		<Box>
																			{layer === 'ODDS' ? (
																				<Typography
																					variant="caption"
																					color="text.secondary"
																					display="block"
																					sx={{ mb: 1 }}
																				>
																					{t('externalApiMonitoring.oddsSlotDetail', {
																						matchday: formatOddsMatchday(detail),
																						scope:
																							formatOddsSlotScope(detail.slotScope) ??
																							'—',
																					})}
																				</Typography>
																			) : null}
																			{detail.errorSummary ? (
																				<Typography
																					sx={{
																						mb: 1,
																						fontSize: '0.8rem',
																						color: isMonitoringWarningSummary(detail.errorSummary)
																							? '#d97706'
																							: '#f43f5e',
																						fontWeight: 600,
																						whiteSpace: 'normal',
																					}}
																				>
																					{formatMonitoringReason(detail.errorSummary)}
																				</Typography>
																			) : null}
																			<CountersBreakdown
																				layer={layer}
																				counters={detail.counters ?? run.counters}
																			/>
																			{failedMatchesFromDetail(detail).length > 0 ? (
																				<Box sx={{ mb: 1 }}>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																						display="block"
																						sx={{ mb: 0.5 }}
																					>
																						{t('externalApiMonitoring.failedMatches')}:
																					</Typography>
																					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
																						{failedMatchesFromDetail(detail).map((m, idx) => (
																							<MatchTeamsWithLogos
																								key={`${m.matchScheduleId ?? 'm'}-${idx}`}
																								home={{ title: m.homeTitle, logoKey: m.homeLogoKey }}
																								away={{ title: m.awayTitle, logoKey: m.awayLogoKey }}
																								height={16}
																								sx={{ fontSize: '0.8rem' }}
																							/>
																						))}
																					</Box>
																				</Box>
																			) : null}
																			{(detail.httpLogs?.length ?? 0) === 0 ? (
																				<Typography variant="caption" color="text.secondary">
																					{t('externalApiMonitoring.noHttpLogs')}
																				</Typography>
																			) : (
																				<Table size="small" sx={monitoringDetailTableSx(theme, layer)}>
																					<TableHead>
																						<TableRow>
																							<TableCell>{t('externalApiMonitoring.http.type')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.target')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.teams')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.status')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.outcome')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.duration')}</TableCell>
																							<TableCell>{t('externalApiMonitoring.http.detail')}</TableCell>
																						</TableRow>
																					</TableHead>
																					<TableBody>
																						{detail.httpLogs!.map((log, idx) => (
																							<TableRow key={`${run.id}-http-${idx}`}>
																								<TableCell>{log.requestType ?? '—'}</TableCell>
																								<TableCell>{log.target ?? '—'}</TableCell>
																								<TableCell
																									sx={{
																										maxWidth: 260,
																										whiteSpace: 'normal',
																										wordBreak: 'break-word',
																									}}
																								>
																									{(() => {
																										const teams = httpLogTeams(log);
																										if (!teams) return '—';
																										return (
																											<MatchTeamsWithLogos
																												home={{
																													title: teams.homeTitle,
																													logoKey: teams.homeLogoKey,
																												}}
																												away={{
																													title: teams.awayTitle,
																													logoKey: teams.awayLogoKey,
																												}}
																												height={16}
																												sx={{ fontSize: '0.78rem' }}
																											/>
																										);
																									})()}
																								</TableCell>
																								<TableCell>{log.httpStatus ?? '—'}</TableCell>
																								<TableCell
																									sx={{
																										color:
																											log.outcome && log.outcome !== 'SUCCESS'
																												? '#f43f5e'
																												: '#22c55e',
																										fontWeight: 600,
																									}}
																								>
																									{log.outcome ?? '—'}
																								</TableCell>
																								<TableCell>{formatDuration(log.durationMs)}</TableCell>
																								<TableCell
																									sx={{
																										maxWidth: 280,
																										overflow: 'hidden',
																										textOverflow: 'ellipsis',
																									}}
																								>
																									{log.detail ?? '—'}
																								</TableCell>
																							</TableRow>
																						))}
																					</TableBody>
																				</Table>
																			)}
																		</Box>
																	) : null}
																</Box>
															</Collapse>
														</TableCell>
													</TableRow>
												</Fragment>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				);
			})}

			{MONITORING_LAYERS.some((layer) => totalsByLayer[layer].hasMore) ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
					<Button variant="outlined" onClick={() => void loadMore()} disabled={loading || loadingMore}>
						{loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
						{t('externalApiMonitoring.loadMore')}
					</Button>
				</Box>
			) : null}

			<CustomCalendarDialog
				open={deleteLayer != null}
				onClose={() => {
					if (!deleting) setDeleteLayer(null);
				}}
				onSave={() => void handleDeleteLayer()}
				title={
					deleteLayer
						? t('externalApiMonitoring.deleteLayerTitle', { layer: layerTitle(deleteLayer) })
						: undefined
				}
				helperText={
					deleteLayer
						? t('externalApiMonitoring.deleteLayerHelper', { layer: layerTitle(deleteLayer) })
						: undefined
				}
				buttonAcceptText={t('externalApiMonitoring.deleteLayerConfirm')}
				submitting={deleting}
			/>
		</Box>
	);
}
